/**
 * Colours for everything Tailwind classes cannot reach.
 *
 * Chart.js canvases, Leaflet map layers and inline SVG all take colour as a
 * JavaScript string, so they were carrying hardcoded hex from the previous
 * identity — twenty-five literals across six files that a theme change in
 * `tailwind.config.ts` silently skipped. They live here now, so the next
 * change is one file.
 *
 * Kinetic ground rules for anything drawn here:
 *  - White ground, so a colour must be dark or saturated enough to hold a
 *    1px line. The lime accent is bright: it works as a large fill, never as
 *    a thin stroke or small label.
 *  - Categorical sets are separated by hue *and* lightness, so they survive
 *    greyscale printing and the most common colour-vision deficiencies.
 *  - Sequential ramps run cool to hot, easy to hard, which is what the
 *    athlete already expects from every other training tool.
 */

/** Core surfaces and ink — mirrors the Tailwind tokens of the same role. */
export const THEME = {
  ground: "#F7F7F5",
  surface: "#FFFFFF",
  border: "#E4E4E1",
  ink: "#111111",
  inkDeep: "#000000",
  inkSoft: "#3A3A3A",
  accent: "#D6FF3F",
  accentLight: "#E6FF7A",
  accentDark: "#4F6B00",
  muted: "#6B6B6B",
  mutedLight: "#8E8E8E",
  danger: "#D32F2F",
} as const;

/** Sports — categorical. Distinct in hue and in lightness. */
export const SPORT_COLOR: Record<string, string> = {
  swim: "#0B7FA8",
  bike: "#111111",
  run: "#E2571E",
  brick: "#6B21A8",
  strength: "#4F6B00",
  mobility: "#8E8E8E",
  rest: "#E4E4E1",
};

/** Training zones — sequential, Z1 easy through Z7 maximal. */
export const ZONE_COLOR: Record<number, string> = {
  1: "#7FB800",
  2: "#00A0A6",
  3: "#F2A104",
  4: "#E2571E",
  5: "#D32F2F",
  6: "#A11B1B",
  7: "#6E1010",
};

/**
 * Interval blocks in a session graphic. Black is the work, lime is the hard
 * effort, grey is everything supporting it — large filled shapes, which is
 * the one place the accent carries at full strength.
 */
export const INTERVAL_COLOR: Record<string, string> = {
  warmup: "#8E8E8E",
  cooldown: "#8E8E8E",
  recovery: "#8E8E8E",
  main: "#111111",
  interval: "#D6FF3F",
};

/** Chart chrome shared by every Chart.js canvas. */
export const CHART = {
  grid: THEME.border,
  tick: THEME.muted,
  legend: THEME.ink,
  tooltipBg: THEME.ink,
  tooltipText: THEME.surface,
  seriesPrimary: THEME.ink,
  seriesSecondary: THEME.accentDark,
} as const;

/**
 * Metric series. Named by what they measure rather than by colour, so the
 * next palette change does not have to re-derive which line was which.
 */
export const SERIES = {
  heartRate: "#D32F2F",
  pace: "#0B7FA8",
  power: "#111111",
  altitude: "#8E8E8E",
  altitudeFill: "rgba(142, 142, 142, 0.22)",
  ctl: "#111111",
  atl: "#E2571E",
  tsb: "#4F6B00",
} as const;

/** Route rendering on the workout map. */
export const MAP = {
  route: THEME.ink,
  start: THEME.accent,
  finish: THEME.danger,
  markerBorder: THEME.ink,
} as const;

/**
 * Pick a readable ink for text sitting on an arbitrary background.
 *
 * Needed because the zone ramp spans from a light lime (Z1) to a near-black
 * red (Z7): a fixed white label is unreadable on the easy zones and a fixed
 * black one is unreadable on the hard ones. Uses the WCAG relative-luminance
 * formula and returns whichever of ink/surface has the better ratio.
 */
export function inkOn(background: string): string {
  const hex = background.replace("#", "");
  const channel = (i: number) => {
    const c = parseInt(hex.slice(i, i + 2), 16) / 255;
    return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  const luminance =
    0.2126 * channel(0) + 0.7152 * channel(2) + 0.0722 * channel(4);
  const onDark = (luminance + 0.05) / 0.05;          // vs #000-ish ink
  const onLight = 1.05 / (luminance + 0.05);         // vs white surface
  return onDark >= onLight ? THEME.ink : THEME.surface;
}
